/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { FaXmark } from "react-icons/fa6";
import toast from "react-hot-toast";
import { delayOrderApi } from "@/api/take-order/take-order";

function getUkTimeParts() {
    const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    const parts = formatter.formatToParts(new Date());
    const hours = Number(parts.find((part) => part.type === "hour")?.value || 0);
    const minutes = Number(parts.find((part) => part.type === "minute")?.value || 0);

    return { hours, minutes };
}

function formatTimeForInput(hours, minutes) {
    const safeHours = `${hours}`.padStart(2, "0");
    const safeMinutes = `${minutes}`.padStart(2, "0");
    return `${safeHours}:${safeMinutes}`;
}

function toMinutesOfDay(hours, minutes) {
    return hours * 60 + minutes;
}

function fromMinutesOfDay(totalMinutes) {
    const normalized = ((totalMinutes % 1440) + 1440) % 1440;
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    return formatTimeForInput(hours, minutes);
}

function parseTimeValue(timeValue) {
    const [hours, minutes] = timeValue.split(":").map((value) => Number(value));
    return { hours, minutes };
}

function DelayOrderModal({ isOpen, onClose, isTabletView, orderId, onSuccess }) {
    const [delayMinutes, setDelayMinutes] = useState("");
    const [pickupTime, setPickupTime] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const nowUk = getUkTimeParts();
        setDelayMinutes("");
        setPickupTime(formatTimeForInput(nowUk.hours, nowUk.minutes));
    }, [isOpen]);

    const handleDelayMinutesChange = (event) => {
        const value = event.target.value;
        setDelayMinutes(value);

        const parsed = Number(value);
        if (Number.isNaN(parsed) || parsed < 0) {
            return;
        }

        const nowUk = getUkTimeParts();
        const nowMinutes = toMinutesOfDay(nowUk.hours, nowUk.minutes);
        const next = fromMinutesOfDay(nowMinutes + parsed);
        setPickupTime(next);
    };

    const handlePickupTimeChange = (event) => {
        const value = event.target.value;
        setPickupTime(value);

        if (!value) {
            setDelayMinutes("");
            return;
        }

        const nowUk = getUkTimeParts();
        const nowMinutes = toMinutesOfDay(nowUk.hours, nowUk.minutes);
        const targetParts = parseTimeValue(value);
        const targetMinutes = toMinutesOfDay(targetParts.hours, targetParts.minutes);
        const diffMinutes = targetMinutes >= nowMinutes
            ? targetMinutes - nowMinutes
            : 1440 - nowMinutes + targetMinutes;
        setDelayMinutes(`${diffMinutes}`);
    };

    const handleSave = async () => {
        if (!orderId) {
            toast.error("Order id not found");
            return;
        }

        if (!pickupTime) {
            toast.error("Please select pickup time");
            return;
        }

        setLoading(true);
        const res = await delayOrderApi(orderId, { pickupTime });
        setLoading(false);

        if (res?.status === "success") {
            toast.success(res?.message || "Order delayed successfully");
            onClose();
            return;
        }

        toast.error(res?.message || "Failed to delay order");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed top-0 left-0 w-screen h-screen bg-black/70 flex items-center justify-center z-50">
            <div className={`${isTabletView ? "w-[360px] p-4" : "w-[520px] p-6"} bg-color2 rounded-md relative border-2 border-color3`}>
                <button onClick={onClose} className="absolute top-2 right-2 text-color8 cursor-pointer">
                    <FaXmark size={20} />
                </button>
                <h2 className={`${isTabletView ? "text-lg" : "text-2xl"} text-center text-color6 font-semibold mb-6`}>Delay Order</h2>
                <div className={`${isTabletView ? "gap-4" : "gap-6"} flex items-center justify-center`}>
                    <div className="flex flex-col items-center gap-2">
                        <span className={`${isTabletView ? "text-sm" : "text-base"} text-white`}>Delay From Now (min)</span>
                        <input
                            type="number"
                            min="0"
                            value={delayMinutes}
                            onChange={handleDelayMinutesChange}
                            className={`${isTabletView ? "w-[120px] p-2 text-sm" : "w-[180px] p-3 text-base"} bg-color3 text-center rounded-md text-white outline-none`}
                            placeholder="0"
                        />
                    </div>
                    <div className={` mt-6 ${isTabletView ? "text-xl" : "text-3xl"} text-white`}>↔</div>
                    <div className="flex flex-col items-center gap-2">
                        <span className={`${isTabletView ? "text-sm" : "text-base"} text-white`}>Pick Up Time (UK)</span>
                        <input
                            type="time"
                            value={pickupTime}
                            onChange={handlePickupTimeChange}
                            className={`${isTabletView ? "w-[120px] p-2 text-sm" : "w-[180px] p-3 text-base"} bg-color3 text-center rounded-md text-white outline-none`}
                        />
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={loading}
                    className={`${isTabletView ? "mt-6 p-2 text-sm" : "mt-8 p-3 text-base"} w-full bg-color7 font-semibold rounded-md text-black`}
                >
                    {loading ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );
}

export default DelayOrderModal;
